import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Percent, Filter, X } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Countdown } from '../components/PromoBanners';
import { getPriceData } from '../utils/currency';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GROUP_ORDER = ['combo', 'multi_buy', 'fixed_price', 'percent', 'third_free', 'second_half', 'buy_x_get_y', 'spend_over'];
const GROUP_TITLES = {
    combo: 'Комбо оферти', multi_buy: 'Количествени отстъпки', fixed_price: 'Промо продукти',
    percent: 'Промо продукти с отстъпка', third_free: 'Купи 2, вземи 3', second_half: 'Втори продукт -50%',
    buy_x_get_y: 'Специални предложения', spend_over: 'Отстъпки над сума',
};

const OfferCard = ({ offer }) => {
    const hb = offer.has_banner || {};
    const img = hb.desktop ? `${API}/promotions/${offer.promo_id}/banner/desktop`
        : hb.mobile ? `${API}/promotions/${offer.promo_id}/banner/mobile`
        : offer.products?.[0]?.image_url;
    const saving = offer.saving || (offer.type === 'percent' && offer.percent_off ? null : null);
    return (
        <Link to={`/offer/${offer.seo?.slug || offer.promo_id}`} className="group border rounded-xl overflow-hidden bg-white dark:bg-slate-800 shadow-sm hover:shadow-lg transition-shadow" data-testid={`offer-card-${offer.promo_id}`}>
            <div className="relative h-40 bg-amber-50 dark:bg-slate-700 overflow-hidden">
                {img && <img src={img} alt={offer.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />}
                <Badge className="absolute top-2 left-2 bg-red-600 text-white">{offer.badge}</Badge>
                {offer.show_countdown && offer.end_at && (
                    <div className="absolute bottom-2 left-2"><Countdown endAt={offer.end_at} compact /></div>
                )}
            </div>
            <div className="p-4">
                <h3 className="font-semibold truncate">{offer.short_title || offer.name}</h3>
                {offer.description && <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{offer.description}</p>}
                {offer.type === 'combo' && offer.combo_price != null && (
                    <div className="mt-2 flex items-baseline gap-2">
                        <span className="text-lg font-bold text-red-600">{getPriceData(offer.combo_price).eurFormatted} €</span>
                        {offer.regular_total > offer.combo_price && <span className="text-sm text-muted-foreground line-through">{getPriceData(offer.regular_total).eurFormatted} €</span>}
                    </div>
                )}
                {saving > 0 && <p className="text-xs text-green-700 font-medium mt-1">Спестявате {getPriceData(saving).eurFormatted} €</p>}
                <p className="text-xs text-amber-700 mt-2">{offer.products?.length || 0} продукта →</p>
            </div>
        </Link>
    );
};

export default function PromotionsPage() {
    const [groups, setGroups] = useState({});
    const [loading, setLoading] = useState(true);
    const [fType, setFType] = useState('all');
    const [fDiscount, setFDiscount] = useState('all');
    const [categories, setCategories] = useState([]);
    const [brands, setBrands] = useState([]);
    const [fCategory, setFCategory] = useState('all');
    const [fBrand, setFBrand] = useState('all');

    useEffect(() => {
        document.title = 'Промоции | Вкусът на Турция';
        window.scrollTo(0, 0);
        Promise.all([
            axios.get(`${API}/promotions/active`),
            axios.get(`${API}/categories`),
            axios.get(`${API}/brands`).catch(() => ({ data: [] })),
        ]).then(([pr, cr, br]) => {
            setGroups(pr.data.groups || {});
            setCategories(cr.data || []);
            setBrands(Array.isArray(br.data) ? br.data : (br.data.brands || []));
        }).finally(() => setLoading(false));
    }, []);

    const discountOf = (o) => {
        if (o.percent_off) return o.percent_off;
        if (o.saving && o.regular_total) return (o.saving / o.regular_total) * 100;
        return 0;
    };

    const matches = (o) => {
        if (fDiscount !== 'all' && discountOf(o) < Number(fDiscount)) return false;
        if (fCategory !== 'all' || fBrand !== 'all') {
            const prods = o.products || [];
            if (fCategory !== 'all' && !prods.some(p => p.category_id === fCategory)) {
                // category info not in payload products; fall back to allowing offers without product category data
                return prods.length === 0;
            }
        }
        return true;
    };

    const visibleGroups = GROUP_ORDER.filter(g => groups[g]?.length && (fType === 'all' || fType === g));
    const hasAny = visibleGroups.some(g => groups[g].filter(matches).length);

    return (
        <div className="min-h-screen bg-amber-50/30 dark:bg-slate-900">
            <div className="max-w-7xl mx-auto px-4 py-8">
                <div className="mb-6">
                    <h1 className="text-3xl sm:text-4xl font-bold flex items-center gap-3" data-testid="promotions-page-title">
                        <Percent className="w-8 h-8 text-red-600" /> Промоции
                    </h1>
                    <p className="text-muted-foreground mt-1">Всички активни оферти и специални предложения</p>
                </div>

                <div className="flex flex-wrap gap-3 mb-6 items-center">
                    <Filter className="w-4 h-4 text-muted-foreground" />
                    <Select value={fType} onValueChange={setFType}>
                        <SelectTrigger className="w-44" data-testid="filter-type"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Всички видове</SelectItem>
                            {GROUP_ORDER.filter(g => groups[g]?.length).map(g => <SelectItem key={g} value={g}>{GROUP_TITLES[g]}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Select value={fDiscount} onValueChange={setFDiscount}>
                        <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Всякаква отстъпка</SelectItem>
                            <SelectItem value="10">Над 10%</SelectItem>
                            <SelectItem value="20">Над 20%</SelectItem>
                            <SelectItem value="30">Над 30%</SelectItem>
                        </SelectContent>
                    </Select>
                    {(fType !== 'all' || fDiscount !== 'all') && (
                        <Button variant="ghost" size="sm" onClick={() => { setFType('all'); setFDiscount('all'); }}><X className="w-3 h-3 mr-1" />Изчисти</Button>
                    )}
                </div>

                {loading ? (
                    <p className="text-muted-foreground">Зареждане...</p>
                ) : !hasAny ? (
                    <div className="text-center py-16">
                        <p className="text-lg font-medium">В момента няма активни промоции</p>
                        <p className="text-muted-foreground text-sm mt-1">Проверете отново скоро — подготвяме нови оферти! 🎉</p>
                        <Link to="/products"><Button className="mt-4">Разгледай продуктите</Button></Link>
                    </div>
                ) : (
                    visibleGroups.map(g => {
                        const items = groups[g].filter(matches);
                        if (!items.length) return null;
                        return (
                            <section key={g} className="mb-10">
                                <h2 className="text-lg font-bold mb-4 border-l-4 border-red-600 pl-3">{GROUP_TITLES[g]}</h2>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                                    {items.map(o => <OfferCard key={o.promo_id} offer={o} />)}
                                </div>
                            </section>
                        );
                    })
                )}
            </div>
        </div>
    );
}
